// src/pages/Users/UserRequestsPage.jsx

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserRequestsThunk,
  handleUserRequestThunk,
  clearUserError,
  deleteUserRequestThunk,
  deleteMultipleUserRequestsThunk,
} from "../../store/thunks/userThunks";
import Filter from "../../components/common/Filter";
import Checkbox from "../../components/common/Checkbox";
import { notifyError } from "../../utils/notification";
import UserRequestCard from "./components/UserRequestCard";
import UserRequestActionModal from "./components/UserRequestActionModal";
import Pagination from "../../components/common/Pagination";
import Tab from "../../components/common/Tab";
import {
  userRequestButtonsConfig,
  userRequestStatusMessages,
  userRequestStatusVariants,
} from "./constants/userRequestConstants";
import ActionButtons from "../../components/PageUI/ActionButtons";

const requestFilterStatus = {
  All: "",
  Pending: "pending",
  Approved: "approved",
  Rejected: "rejected",
  Completed: "complete",
};

const UserRequestsPage = () => {
  const dispatch = useDispatch();
  const { userRequests, requestTypes, loading, error, totalPages } =
    useSelector((state) => state.user);
  const [currentFilter, setCurrentFilter] = useState("Pending");
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState("");
  const [modalRequestIds, setModalRequestIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentRequestType, setCurrentRequestType] = useState("");

  useEffect(() => {
    if (error) {
      notifyError(error);
      dispatch(clearUserError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    const payload = {
      status: requestFilterStatus[currentFilter],
      page: currentPage,
      requestType: currentRequestType,
    };

    dispatch(getUserRequestsThunk(payload));
  }, [dispatch, currentFilter, currentPage, currentRequestType]);

  useEffect(() => {
    setSelectAll(
      selectedRequests.length === userRequests.length && userRequests.length > 0
    );
  }, [selectedRequests, userRequests]);

  const filteredRequests = currentRequestType
    ? userRequests.filter((req) => req.requestType === currentRequestType)
    : userRequests;

  const handleFilterChange = (status) => {
    setCurrentFilter(status);
    setSelectedRequests([]);
    setSelectAll(false);
    setCurrentPage(1);
  };

  const handleTabChange = (label) => {
    setCurrentRequestType(label);
  };

  const handleSelectRequest = (requestId) => {
    setSelectedRequests((prevSelected) =>
      prevSelected.includes(requestId)
        ? prevSelected.filter((id) => id !== requestId)
        : [...prevSelected, requestId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRequests([]);
    } else {
      setSelectedRequests(userRequests.map((request) => request._id));
    }
    setSelectAll(!selectAll);
  };

  const openModal = (action, requestIds) => {
    setModalAction(action);
    setModalRequestIds(requestIds);
    setIsModalOpen(true);
  };

  const handleBulkAction = (action) => {
    if (action === "delete") {
      handleBulkDelete();
    } else {
      openModal(action, selectedRequests);
    }
  };

  const handleSingleAction = (requestId, action) => {
    openModal(action, [requestId]);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalAction("");
    setModalRequestIds([]);
  };

  const handleModalSubmit = (adminMessage) => {
    const promises = modalRequestIds.map((requestId) => {
      const actionPayload = {
        requestId,
        approve: modalAction === "approve",
        complete: modalAction === "complete",
        adminMessage,
      };

      return dispatch(handleUserRequestThunk(actionPayload));
    });

    Promise.all(promises).then(() => {
      dispatch(
        getUserRequestsThunk({
          status: requestFilterStatus[currentFilter],
          page: currentPage,
          requestType: currentRequestType,
        })
      );
      closeModal();
    });
  };

  const handleDeleteRequest = (requestId) => {
    if (window.confirm("Are you sure you want to delete this request?")) {
      dispatch(deleteUserRequestThunk(requestId)).then(() => {
        dispatch(
          getUserRequestsThunk({
            status: requestFilterStatus[currentFilter],
            page: currentPage,
            requestType: currentRequestType,
          })
        );
      });
    }
  };

  const handleBulkDelete = () => {
    if (
      window.confirm("Are you sure you want to delete the selected requests?")
    ) {
      dispatch(deleteMultipleUserRequestsThunk(selectedRequests)).then(() => {
        dispatch(
          getUserRequestsThunk({
            status: requestFilterStatus[currentFilter],
            page: currentPage,
            requestType: currentRequestType,
          })
        );
        setSelectedRequests([]);
        setSelectAll(false);
      });
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedRequests([]);
    setSelectAll(false);
  };

  const renderBulkActionButtons = () => {
    const isDisabled = selectedRequests.length === 0;

    let requestType = currentRequestType || "All";

    if (selectedRequests.length > 0) {
      const request = userRequests.find(
        (req) => req._id === selectedRequests[0]
      );
      if (request && request.requestType) {
        requestType = request.requestType;
      }
    }

    let config =
      typeof userRequestButtonsConfig[currentFilter] === "function"
        ? userRequestButtonsConfig[currentFilter](requestType)
        : userRequestButtonsConfig[currentFilter] ||
          userRequestButtonsConfig.All;

    return (
      <ActionButtons
        buttonsConfig={config}
        onAction={handleBulkAction}
        size="md"
        disabled={isDisabled}
      />
    );
  };

  return (
    <div className="container mx-auto p-4 bg-background text-text">
      <h1 className="text-2xl font-bold mb-4">Customer Requests</h1>
      <div className="sticky top-0 z-6 bg-background pt-2 ">
        <div className="flex flex-col lg:flex-row justify-between items-center mb-4">
          <Filter
            defaultOption="Pending"
            options={["All", "Pending", "Approved", "Rejected", "Completed"]}
            onSelect={handleFilterChange}
          />
          <div className="flex gap-2 items-center justify-center mt-2 lg:mt-0">
            <div>{`Sel: ${selectedRequests.length}`}</div>
            <Checkbox
              label=""
              name="select-all"
              checked={selectAll}
              onChange={handleSelectAll}
              size="lg"
              className="mx-2 mr-4"
            />
            {renderBulkActionButtons()}
          </div>
        </div>
        {requestTypes.length > 0 && (
          <Tab
            tabs={requestTypes.map((type) => ({
              label: type,
              content: null,
            }))}
            onTabChange={handleTabChange}
          />
        )}
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredRequests.map((request) => (
            <UserRequestCard
              key={request._id}
              request={request}
              handleSelectRequest={handleSelectRequest}
              handleSingleAction={handleSingleAction}
              handleDeleteRequest={handleDeleteRequest}
              selected={selectedRequests.includes(request._id)}
            />
          ))}
        </div>
      )}
      {!loading && filteredRequests.length === 0 && (
        <p>No Customer Requests Found For {currentFilter}</p>
      )}
      {!loading && filteredRequests.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
      <UserRequestActionModal
        isOpen={isModalOpen}
        closeModal={closeModal}
        action={modalAction}
        onSubmit={handleModalSubmit}
      />
    </div>
  );
};

export default UserRequestsPage;
